import { useState, useEffect } from 'react';
import axios from 'axios';
import { JournalEntry as JournalEntryType, LineItem, RecurringSchedule, RenewalInfo } from '../types/accounting';

// Rename the local interface to avoid conflict
export interface JournalEntryData {
  id: string;
  date: Date;
  type: string;
  description: string;
  status: 'pending' | 'approved' | 'rejected' | 'posted';
  fiscalCategory?: {
    cause?: string;
    campaign?: string;
    fundType?: string;
    restrictionType?: string;
  };
  accountCategory: string;
  subCategory: string;
  accountCode: string;
  taxCategory: string;
  isRecurring: boolean;
  documents?: Array<{
    type: string;
    name: string;
    url: string;
  }>;
}

export const useJournalEntries = () => {
  const [entries, setEntries] = useState<JournalEntryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEntries = async () => {
    try {
      const response = await axios.get<JournalEntryData[]>('/api/journal-entries/list');
      setEntries(response.data);
    } catch (err) {
      setError('Failed to fetch journal entries');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const addEntry = async (entryData: Omit<JournalEntryData, 'id'>) => {
    try {
      const response = await axios.post<JournalEntryData>('/api/journal-entries/submit', entryData);
      setEntries(prev => [...prev, response.data]);
      return response.data;
    } catch (err) {
      console.error('Failed to add journal entry:', err);
      throw new Error('Failed to add journal entry');
    }
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  return {
    entries,
    loading,
    error,
    addEntry,
    refreshEntries: fetchEntries
  };
};